import { J2Document } from '@e3dc-react/icons'
import { Box, Button, Card, CardMedia, Collapse, TableCell, TableRow, Typography } from '@mui/material'
import { currencyFormatter } from 'src/Libs/shop/utils/formatter'
import React, { useEffect, useState } from 'react'
import CloseIcon from '@mui/icons-material/Close'
import { makeStyles } from 'tss-react/mui'
import { LineItem, ShopCheckoutSideBarResponse } from '../../models'
import { useDebounce } from '@e3dc-react/shell/Hooks/useDebounce'
import { LoadingButton } from '@mui/lab'
import { PRODUCT_CART_DEBOUNCE_TIMER } from '../../consts'
import { getImageUrlBySize } from '../../utils'
import { ProductType, ShopImageThumnbailSizes } from '../../types'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { ProductGeneralType, ShopSystemConfigurationTypes } from 'src/Libs/shop/types/types'
import { checkProductForSmartMetersAvailable, getSmartMeterQuantityInformation } from '../../utils/smart-meter'
import useModals from 'src/Hooks/useModals'
import ConfirmCheckoutConfigureSystemSubProduct from './ConfirmCheckoutConfigureSystemSubProducts'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import ButtonQuantityGroup from '../ShopProduct/ButtonQuantityGroup'

interface ConfirmCheckoutSingleProductProps {
  isLoading: boolean
  product: LineItem
  onUpdateProduct: (productId: string, quantity: number) => void
  onDeleteProduct: (productId: string) => void
  handleCartResponse: (cartData: ShopCheckoutSideBarResponse | null) => void
}

interface StyleProps {
  isDocumentsReady: boolean
  quantityType: ShopSystemConfigurationTypes
}

const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  noBorder: {
    borderBottom: 'none',
  },
  productContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    whiteSpace: 'nowrap',
  },
  productNameContainer: {
    minWidth: '350px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  productQuantityContianer: {
    backgroundColor: props.quantityType === ShopSystemConfigurationTypes.Fix ? theme.palette.primary.light : theme.palette.warning.light,
    color: props.quantityType === ShopSystemConfigurationTypes.Fix ? theme.palette.primary.main : theme.palette.warning.main,
    marginLeft: theme.spacing(2),
    padding: `0 ${theme.spacing(2)}`,
    borderRadius: theme.shape.borderRadius,
  },
  productWrapper: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    whiteSpace: 'nowrap',
  },
  productImage: {
    minWidth: '75px',
    maxWidth: '75px',
    height: 'auto',
    objectFit: 'contain',
    backgroundColor: theme.palette.shades.shade03,
    marginRight: theme.spacing(2),
  },
  productInfo: {
    display: 'block',
  },
  closeIcon: {
    border: `1px solid ${theme.palette.shades.shade02}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.shades.shade04,
    color: theme.palette.grey.grey02,
    minWidth: '36px',
    minHeight: '36px',
    marginLeft: theme.spacing(4),
    padding: 0,
  },
  documentIcon: {
    backgroundColor: props.isDocumentsReady ? theme.palette.common.white : theme.palette.error.main,
    border: props.isDocumentsReady ? `1px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.error.main}`,
    color: props.isDocumentsReady ? theme.palette.primary.main : theme.palette.common.white,
    '&:hover': {
      color: props.isDocumentsReady ? theme.palette.primary.main : theme.palette.error.main,
    },
  },
  spaceBetweenCenter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  productLabel: {
    maxWidth: '450px',
    whiteSpace: 'break-spaces',
  },
  paperContainer: {
    marginLeft: theme.spacing(14),
    background: theme.palette.background.paper2,
    marginBottom: theme.spacing(1),
  },
  collapsedRow: {
    '& > *': {
      padding: 0,
      borderBottom: 'none',
    },
  },
}))

const ConfirmCheckoutSingleProduct: React.FC<ConfirmCheckoutSingleProductProps> = ({
  isLoading,
  product,
  onDeleteProduct,
  onUpdateProduct,
  handleCartResponse,
}) => {
  const { t } = useCustomTranslation()
  const [quantity, setQuantity] = useState<number>(product.quantity)
  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  const debouncedQuantity = useDebounce(quantity, PRODUCT_CART_DEBOUNCE_TIMER)
  const productTotalPrice = currencyFormatter(product.price.totalPrice)
  const pricePerProduct = currencyFormatter(product.price?.unitPrice)
  const disableMinQuantity = product?.quantityInformation?.minPurchase
  const disableMaxQuantity = product?.quantityInformation?.maxPurchase
  const productImageUrl = getImageUrlBySize(product.cover?.thumbnails, ShopImageThumnbailSizes.Small)
  const isProductTypeRetrofitBattery = product.payload.e3dc?.productType === ProductType.RetrofitBattery
  const isProductTypePromoCode = product.type === ProductGeneralType.PROMOTION
  const isProductTypeSystemConfiguration =
    product.type === ProductGeneralType.SYSTEM_CONFIGURATOR || product.type === ProductGeneralType.QNTM_SYSTEM_CONFIGURATOR
  const retrofitBatterySerialNumber = product.payload.e3dc?.serialNumber
  const productSmartMeterAvailable = checkProductForSmartMetersAvailable(product)
  const smartMeterQuantityInformation = getSmartMeterQuantityInformation(product)
  const smartMeterQuantity = smartMeterQuantityInformation?.smartMeterQuantity
  const smartMeterDocumentsQuantity = smartMeterQuantityInformation?.smartMeterDocumentsQuantity
  const isDocumentsReady = smartMeterQuantity === smartMeterDocumentsQuantity
  const quantityType = product.payload.quantityType
  const removable = product.removable
  const isQuantityChangeShown = !isProductTypePromoCode && removable
  const expandText = isExpanded ? t('shop.confirm-checkout.collapse-label') : t('shop.confirm-checkout.expand-label')
  const { openShopSmartMeterDocumentUploadModal } = useModals()
  const { classes, cx } = useStyles({ isDocumentsReady, quantityType })

  useEffect(() => {
    if (product.quantity !== debouncedQuantity) {
      if (debouncedQuantity >= 1) {
        onUpdateProduct(product.id, debouncedQuantity)
      }
      if (debouncedQuantity <= 0) {
        onDeleteProduct(product.id)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuantity])

  return (
    <>
      <TableRow>
        <TableCell className={classes.noBorder}>
          <Box className={classes.productContainer}>
            <Box className={classes.productWrapper}>
              <CardMedia className={classes.productImage} component="img" image={productImageUrl} />
              <Box className={classes.productInfo}>
                <Box className={classes.productNameContainer}>
                  <Typography className={classes.productLabel}>{product.label}</Typography>
                  <Typography className={classes.productQuantityContianer}> {quantityType}</Typography>
                </Box>
                {isProductTypeRetrofitBattery && retrofitBatterySerialNumber && (
                  <Typography variant="body2">{`${t('shop.confirm-checkout.serial-number-label')}: ${retrofitBatterySerialNumber}`}</Typography>
                )}
                {isProductTypeSystemConfiguration && (
                  <Button sx={{ paddingLeft: 0 }} variant="text" onClick={() => setIsExpanded(prev => !prev)}>
                    <KeyboardArrowDownIcon
                      sx={{
                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.3s',
                      }}
                    />
                    <Typography variant="inherit">{expandText}</Typography>
                  </Button>
                )}
              </Box>
            </Box>
            {productSmartMeterAvailable && (
              <LoadingButton
                className={cx(classes.closeIcon, classes.documentIcon)}
                disabled={isLoading}
                onClick={() => {
                  openShopSmartMeterDocumentUploadModal({
                    smartMeterCartId: productSmartMeterAvailable.smartMeterId,
                    documentInfo: productSmartMeterAvailable.smartMeterDocuments,
                    smartMeterQuantity: smartMeterQuantityInformation,
                    onCartResponse: cartData => {
                      handleCartResponse(cartData)
                    },
                  })
                }}
              >
                <J2Document />
              </LoadingButton>
            )}
          </Box>
        </TableCell>
        <TableCell className={classes.noBorder}>
          {isQuantityChangeShown && (
            <ButtonQuantityGroup
              quantity={quantity}
              setQuantity={quantity => {
                setQuantity(quantity)
              }}
              minQuantity={disableMinQuantity}
              maxQuantity={disableMaxQuantity}
              isLoading={isLoading}
            />
          )}
        </TableCell>
        <TableCell className={classes.noBorder}>
          <Typography sx={{ whiteSpace: 'nowrap' }}>{pricePerProduct}</Typography>
        </TableCell>
        <TableCell className={classes.noBorder}>
          {removable && (
            <Box className={classes.spaceBetweenCenter}>
              <Typography sx={{ whiteSpace: 'nowrap' }}>{productTotalPrice}</Typography>
              <LoadingButton
                className={classes.closeIcon}
                disabled={isLoading}
                onClick={() => {
                  onDeleteProduct(product.id)
                }}
              >
                <CloseIcon sx={{ width: '20px', height: '20px' }} />
              </LoadingButton>
            </Box>
          )}
        </TableCell>
      </TableRow>
      {product.children.length > 0 && (
        <TableRow className={classes.collapsedRow}>
          <TableCell colSpan={4}>
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <Card variant="outlined" className={classes.paperContainer}>
                <Typography variant="subtitle2" sx={{ marginTop: 2 }}>
                  {t('shop.confirm-checkout.sub-product-label')}
                </Typography>
                {product.children.map(configuratorProduct => {
                  const configuratorQuantity = product.quantity
                  return (
                    <Card key={configuratorProduct.id} sx={{ marginTop: 2, paddingTop: 2, paddingBottom: 2 }}>
                      <ConfirmCheckoutConfigureSystemSubProduct
                        key={configuratorProduct.id}
                        product={configuratorProduct}
                        configuratorQuantity={configuratorQuantity}
                      />
                    </Card>
                  )
                })}
              </Card>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

export default ConfirmCheckoutSingleProduct
